import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { transportApi } from '@/services/transportApi';

const EMPTY = { name: '', phone: '', licence_no: '', licence_expiry: '', usual_vehicle_id: '', user_id: '', active: true };

const Drivers = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [d, v] = await Promise.all([transportApi.listDrivers(), transportApi.listVehicles({ active: true })]);
    setDrivers(d); setVehicles(v);
    try { setUsers(await transportApi.listUsers()); } catch { /* optional */ }
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load drivers')); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d }); setEditId(d.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = { ...form, usual_vehicle_id: form.usual_vehicle_id || null, user_id: form.user_id || null, licence_expiry: form.licence_expiry || null };
      if (editId) await transportApi.updateDriver(editId, payload); else await transportApi.createDriver(payload);
      toast.success('Driver saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Deactivate this driver?')) return; await transportApi.deleteDriver(id); load(); };
  const vlabel = (id) => vehicles.find(v => v.id === id)?.registration_no || '—';
  const ulabel = (id) => { const u = users.find(u => (u.user_id || u.id) === id); return u ? (u.name || u.staff_id || u.email) : (id ? 'linked' : '—'); };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-drivers-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold font-display">Driver Master</h1>
          </div>
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600" data-testid="driver-add-btn"><Plus className="w-4 h-4 mr-1" />Add Driver</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card className="border-0 shadow-md"><CardContent className="p-0">
          <Table data-testid="drivers-table">
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Licence</TableHead>
              <TableHead>Expiry</TableHead><TableHead>Usual Vehicle</TableHead><TableHead>Login</TableHead><TableHead>Active</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {drivers.map(d => (
                <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
                  <TableCell className="font-semibold">{d.name}</TableCell>
                  <TableCell>{d.phone || '—'}</TableCell>
                  <TableCell>{d.licence_no || '—'}</TableCell>
                  <TableCell>{d.licence_expiry || '—'}</TableCell>
                  <TableCell>{vlabel(d.usual_vehicle_id)}</TableCell>
                  <TableCell className="text-xs">{ulabel(d.user_id)}</TableCell>
                  <TableCell>{d.active ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`driver-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(d.id)} data-testid={`driver-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {drivers.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8" data-testid="drivers-empty">No drivers yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="driver-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Driver</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="driver-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="driver-phone-input" /></div>
              <div><Label>Licence No.</Label><Input value={form.licence_no} onChange={e => setForm(f => ({ ...f, licence_no: e.target.value }))} data-testid="driver-licence-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Licence Expiry</Label><Input type="date" value={form.licence_expiry || ''} onChange={e => setForm(f => ({ ...f, licence_expiry: e.target.value }))} data-testid="driver-expiry-input" /></div>
              <div><Label>Usual Vehicle</Label>
                <Select value={form.usual_vehicle_id || 'none'} onValueChange={v => setForm(f => ({ ...f, usual_vehicle_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="driver-vehicle-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Link to Login User (so the driver sees "My Tasks")</Label>
              <Select value={form.user_id || 'none'} onValueChange={v => setForm(f => ({ ...f, user_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="driver-user-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="max-h-60"><SelectItem value="none">None</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id || u.id} value={u.user_id || u.id}>{u.name} {u.staff_id ? `(${u.staff_id})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="driver-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="driver-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Drivers;
